import {useAppDispatch, useAppSelector} from '@/hooks';
import type {Notification, NotificationAJAXResponse, NotificationSettingsPanelData} from '@Panels/notifications-panel/notifications-panel.types';
import {NotificationsPanelDisplayType, NotificationStatus, type NotificationUpdateDispatchType} from '@Panels/notifications-panel/notifications-panel.types';
import {
  setAllNotifications,
  setAllNotificationsDataFromAjax,
  setLoadMore,
  setNewNotificationsCount,
  setNotificationSettings,
  setPanelDisplayType,
  setStartFetchingNotifications,
  updateNotificationsAfterStatusUpdate,
} from '@Panels/notifications-panel/notifications-panel-slice';
import {
  areAllNotificationsTurnedOff,
  bucketizeNotifications,
  convertNotificationsToStatus,
  doesUserHaveNoNotifications,
  getTotalCountOfNotifications,
  NOTIFICATION_FETCH_LIMIT,
  updateNotificationSettings,
} from '@Libraries/notifications-panel-library';
import type {AppDispatch} from '@/store';

export const useNewNotifications = (): Notification[] => {
  return useAppSelector((state) => {
    return state.notificationsPanel.newNotifications;
  });
};

export const useTodaysNotifications = (): Notification[] => {
  return useAppSelector((state) => {
    return state.notificationsPanel.todaysNotifications;
  });
};

export const useYesterdaysNotifications = (): Notification[] => {
  return useAppSelector((state) => {
    return state.notificationsPanel.yesterdaysNotifications;
  });
};
export const useOtherNotifications = (): Notification[] => {
  return useAppSelector((state) => {
    return state.notificationsPanel.otherNotifications;
  });
};

export const useNotificationPanelState = (): NotificationsPanelDisplayType => {
  return useAppSelector((state) => {
    return state.notificationsPanel.panelDisplayType;
  });
};

export const useNotificationSettingsPanelData = (): NotificationSettingsPanelData => {
  return useAppSelector((state) => {
    return state.notificationsPanel.notificationSettings;
  });
};

export const useNotificationPreviousSettingsPanelData = (): NotificationSettingsPanelData => {
  return useAppSelector((state) => {
    return state.notificationsPanel.previousNotificationSettings;
  });
};

export const useAllNotifications = (): Notification[] => {
  return useAppSelector((state) => {
    return state.notificationsPanel.allNotifications;
  });
};

export const useStartFetchingNotifications = (): boolean => {
  return useAppSelector((state) => {
    return state.notificationsPanel.startFetchingNotifications;
  });
};

export const useNewNotificationsCount = (): number => {
  return useAppSelector((state) => {
    return state.notificationsPanel.newNotificationsCount;
  });
};

export const useCanLoadMoreNotifications = (): boolean => {
  return useAppSelector((state) => {
    return state.notificationsPanel.loadMore;
  });
};

export const useUpdateNotificationsState = () => {
  const dispatch = useAppDispatch();

  return (allBuckets: NotificationUpdateDispatchType, updatedNotifications: Notification[]): void => {
    dispatch(updateNotificationsAfterStatusUpdate(allBuckets));
    dispatch(setAllNotifications(updatedNotifications));
  };
};

export const useMarkAllNewNotificationsAsSeen = (): ((allNotifications: Notification[]) => void) => {
  const updateNotificationsState = useUpdateNotificationsState();
  const notificationSettings = useNotificationSettingsPanelData();
  const previousNotificationSettings = useNotificationPreviousSettingsPanelData();

  return (allNotifications: Notification[]): void => {
    const updatedFilteredNotifications = allNotifications.map((n) => (n.status === NotificationStatus.NEW ? convertNotificationsToStatus([n], NotificationStatus.SEEN)[0] : n));
    const allBuckets = bucketizeNotifications(updatedFilteredNotifications);

    updateNotificationSettings(notificationSettings, previousNotificationSettings);

    void window.PMW.writeLocal('notifications/markAllNewNotificationAsSeen').then(() => {
      updateNotificationsState(allBuckets, updatedFilteredNotifications);
    });
  };
};

export const useMarkAllNotificationsAsRead = (): ((allNotifications: Notification[]) => void) => {
  const updateNotificationsState = useUpdateNotificationsState();

  return (allNotifications: Notification[]): void => {
    const allReadNotifications = convertNotificationsToStatus(allNotifications, NotificationStatus.READ);
    const allBuckets = bucketizeNotifications(allReadNotifications);

    void window.PMW.writeLocal('notifications/markAllNotificationsAsRead')
      .then(() => {
        updateNotificationsState(allBuckets, allReadNotifications);
      })
      .catch((e: unknown) => {
        console.error('Marking all notifications as read failed due to: ', e);
      });
  };
};

const handleNotificationsAJAXResponse = (response: NotificationAJAXResponse, allNotifications: Notification[], dispatch: AppDispatch): void => {
  if (response.notificationSettings && areAllNotificationsTurnedOff(response.notificationSettings)) {
    dispatch(setPanelDisplayType(NotificationsPanelDisplayType.TURN_ON_NOTIFICATIONS));
  } else if (doesUserHaveNoNotifications(response) && allNotifications.length === 0) {
    dispatch(setPanelDisplayType(NotificationsPanelDisplayType.EMPTY));
    dispatch(setNotificationSettings(response.notificationSettings));
  } else {
    dispatch(setNewNotificationsCount(response.newNotificationsCount));
    dispatch(setAllNotificationsDataFromAjax(response));
    dispatch(setPanelDisplayType(NotificationsPanelDisplayType.LOADED));
  }
};

const handleFetchNotificationsSuccessResponse = (response: NotificationAJAXResponse, allNotifications: Notification[], dispatch: AppDispatch): void => {
  if (!response && allNotifications.length === 0) {
    dispatch(setPanelDisplayType(NotificationsPanelDisplayType.EMPTY));
    return;
  }

  if (getTotalCountOfNotifications(response) < NOTIFICATION_FETCH_LIMIT) {
    dispatch(setLoadMore(false));
  }

  handleNotificationsAJAXResponse(response, allNotifications, dispatch);
};

export const handleFetchNotificationsFailedResponse = (message: string, e: unknown, dispatch: AppDispatch): void => {
  console.error(message, e);
  dispatch(setPanelDisplayType(NotificationsPanelDisplayType.EMPTY));
};

export const useFetchLazyLoadedNotifications = (): VoidFunction => {
  const dispatch = useAppDispatch();
  const allNotifications = useAllNotifications();
  const startFetchingNotifications = useStartFetchingNotifications();

  return (): void => {
    try {
      if (!startFetchingNotifications) {
        return;
      }

      if (allNotifications.length === 0) {
        dispatch(setPanelDisplayType(NotificationsPanelDisplayType.LOADING));
      }

      void window.PMW.readLocal('notifications/getAllNotificationsForUserWithOffset', {offset: allNotifications.length})
        .then((response: NotificationAJAXResponse) => {
          handleFetchNotificationsSuccessResponse(response, allNotifications, dispatch);
        })
        .catch((e: unknown) => {
          if (allNotifications.length === 0) {
            handleFetchNotificationsFailedResponse('Failed to fetch notifications:', e, dispatch);
          } else {
            console.error('Failed to fetch more notifications:', e);
            dispatch(setLoadMore(false));
          }
        })
        .finally(() => {
          dispatch(setStartFetchingNotifications(false));
        });
    } catch (e) {
      if (allNotifications.length === 0) {
        handleFetchNotificationsFailedResponse('Failed to initialize notifications:', e, dispatch);
      } else {
        console.error('Failed to initialize notifications:', e);
        dispatch(setLoadMore(false));
      }
    }
  };
};
